/** Shared UI, all token-styled (no hardcoded hex/px). */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
// `create` only — the matrix, not a renderer. `toCanvas`/`toDataURL` would pull the canvas and PNG
// paths into the bundle for a picture we draw as one SVG path in ten lines.
import { create as qrCreate } from "qrcode";
import { LOCALES, getLocale, setLocale, type Locale } from "./i18n.js";
import { t } from "./i18n.js";
import { donutSlices, ringPath } from "./donut.js";
import { answeredIn, api, barTotal, formatMean, isWithheldList, meanOf, type LimitError, type McOption, type QuestionAggregate } from "./api.js";

export function LocaleSwitcher(): React.ReactElement {
  const [loc, setLoc] = React.useState<Locale>(getLocale());
  return (
    <div className="flex gap-2 text-sm" role="group" aria-label="Idioma">
      {LOCALES.map((l) => (
        <button
          key={l}
          onClick={() => {
            setLocale(l);
            setLoc(l);
            location.reload();
          }}
          aria-pressed={l === loc}
          className={`rounded-md px-2 py-1 ${l === loc ? "bg-primary text-primary-foreground font-bold" : "text-accent"}`}
        >
          {l.split("-")[0]!.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/** Top bar for authenticated app screens: brand + host/kit nav + logout + locale. */
function TopBar(): React.ReactElement {
  const navigate = useNavigate();
  const logout = async (): Promise<void> => {
    await api.logout();
    navigate("/");
  };
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
      <nav className="flex items-center gap-4">
        <Link to="/" className="font-display text-display-sm">Klapzo</Link>
        <Link to="/host" className="text-accent">{t("topbar.host")}</Link>
        <Link to="/kit" className="text-accent">{t("topbar.kit")}</Link>
      </nav>
      <div className="flex items-center gap-3">
        <LocaleSwitcher />
        <button onClick={() => void logout()} className="rounded-md border border-border px-3 py-1 text-sm">
          {t("topbar.logout")}
        </button>
      </div>
    </header>
  );
}

/** App shell for host/participant screens (centered, prose-width). */
export function Shell({ children, bare }: { children: React.ReactNode; bare?: boolean }): React.ReactElement {
  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6">
      {bare ? (
        <header className="flex items-center justify-between">
          <Link to="/" className="font-display text-display-sm">Klapzo</Link>
          <LocaleSwitcher />
        </header>
      ) : (
        <TopBar />
      )}
      <main className="flex flex-col gap-6">{children}</main>
    </div>
  );
}

/** Kit shell with library/agenda nav. */
export function KitShell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-6 p-6">
      <header className="no-print flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <nav className="flex items-center gap-4">
          <Link to="/" className="font-display text-display-sm">Klapzo</Link>
          <Link to="/kit" className="text-accent">{t("nav.library")}</Link>
          <Link to="/kit/planner" className="text-accent">{t("nav.planner")}</Link>
        </nav>
        <LocaleSwitcher />
      </header>
      <main className="flex flex-col gap-6">{children}</main>
    </div>
  );
}

/**
 * A tally bar. `highlight` marks the correct option on reveal — and it is NOT colour alone: the
 * green fill is paired with a visible "✓" and a spoken label, because a colour-only encoding tells
 * a colourblind viewer nothing about which answer was right, whatever the palette (P10).
 */
export function Bar({ label, value, total, highlight }: { label: string; value: number; total: number; highlight?: boolean }): React.ReactElement {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-sm">
        <span>
          {highlight && <span aria-hidden="true">{"\u2713 "}</span>}
          {label}
          {highlight && <span className="sr-only"> ({t("a11y.correct")})</span>}
        </span>
        <span className="font-bold">{value}</span>
      </div>
      <div data-print-track className="h-6 w-full overflow-hidden rounded-md bg-surface">
        <div
          data-print-bar
          className={`h-full ${highlight ? "bg-success" : "bg-secondary"}`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

export type TallyMode = "bars" | "donut";

/** Slice fills, all design-system tokens. Colour is never the only channel: the legend carries
 *  label, count and percent for every slice (THE-6011 AC3). */
const SLICE_FILLS = ["fill-secondary", "fill-primary", "fill-success", "fill-warning", "fill-accent"] as const;
const SWATCH_BG = ["bg-secondary", "bg-primary", "bg-success", "bg-warning", "bg-accent"] as const;

/**
 * An mc tally as a ring + legend. Same data and the same denominator as the bars (`barTotal`).
 * The ring is `aria-hidden`: the legend is the accessible reading of it.
 */
export function Donut({
  options,
  counts,
  total,
  correctOptionIds,
}: {
  options: readonly { id: string; label: string }[];
  counts: Record<string, number>;
  total: number;
  correctOptionIds?: readonly string[] | undefined;
}): React.ReactElement {
  const slices = donutSlices(options.map((o) => ({ id: o.id, value: counts[o.id] ?? 0 })), total);
  return (
    <div className="flex flex-wrap items-center gap-6" data-donut>
      <svg viewBox="0 0 100 100" className="h-48 w-48 shrink-0" aria-hidden="true" focusable="false">
        <circle cx="50" cy="50" r="32.5" className="fill-none stroke-surface" strokeWidth="15" />
        {slices.map((sl, i) => (
          <path
            key={sl.id}
            d={ringPath(50, 50, 40, 25, sl.start, sl.end)}
            className={`${SLICE_FILLS[i % SLICE_FILLS.length]!} stroke-background`}
            strokeWidth="0.5"
          />
        ))}
      </svg>
      <ul className="flex flex-col gap-2">
        {options.map((o, i) => {
          const sl = slices[i]!;
          const correct = Boolean(correctOptionIds?.includes(o.id));
          return (
            <li key={o.id} className="flex items-center gap-2">
              <span aria-hidden="true" className={`inline-block h-4 w-4 rounded-sm ${SWATCH_BG[i % SWATCH_BG.length]!}`} />
              <span>
                {correct && <span aria-hidden="true">{"\u2713 "}</span>}
                {o.label}
                {correct && <span className="sr-only"> ({t("a11y.correct")})</span>}
              </span>
              <span className="font-bold">
                {sl.value} ({sl.pct}%)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The open mc tally, as bars (default) or a ring, with the switch. A withheld tally renders nothing
 * here at all — no slice, no bar — exactly like the bars always did; the caller's own "hidden" block
 * says how many answered. The mode is local state: nothing crosses the wire.
 */
export function McTally({
  options,
  agg,
  initialMode = "bars",
}: {
  options: readonly { id: string; label: string }[];
  agg: Extract<QuestionAggregate, { type: "mc" }>;
  initialMode?: TallyMode;
}): React.ReactElement | null {
  const [mode, setMode] = React.useState<TallyMode>(initialMode);
  if (agg.tally !== "open") return null;
  const total = barTotal(agg);
  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label={t("mc.viewLabel")} className="flex gap-2 text-sm print:hidden">
        {(["bars", "donut"] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={`rounded-md px-3 py-1 ${mode === m ? "bg-secondary font-bold text-secondary-foreground" : "bg-surface"}`}
          >
            {t(m === "bars" ? "mc.viewBars" : "mc.viewDonut")}
          </button>
        ))}
      </div>
      {mode === "bars" ? (
        options.map((o) => (
          <Bar
            key={o.id}
            label={o.label}
            value={agg.counts[o.id] ?? 0}
            // Points, not people, on a priority round — see `barTotal`.
            total={total}
            highlight={Boolean(agg.correctOptionIds?.includes(o.id))}
          />
        ))
      ) : (
        <Donut options={options} counts={agg.counts} total={total} correctOptionIds={agg.correctOptionIds} />
      )}
    </div>
  );
}

/**
 * "n frases ficaram de fora do sorteio da urna" (W10a, US-LV-17 AC3).
 *
 * One component for the three screens that must say it — projection, presenter and the phone's own
 * ballot — because the phone is the one that matters: an author whose line is missing learns it was
 * a draw, not a rejection. Renders nothing when the round fit, which is every round under the cap.
 */
// `n: number | undefined`, not `n?: number`: every caller passes the aggregate's field, which is
// absent on a round that fit — `exactOptionalPropertyTypes` makes those two different types.
export function OmittedNote({ n, className }: { n: number | undefined; className?: string }): React.ReactElement | null {
  if (!n) return null;
  // `className` because the same sentence has two type sizes: 16px is right on a phone and
  // illegible on a three-metre wall, and the projection passes display type.
  return (
    <p className={className ?? "text-accent"}>
      {t(n === 1 ? "pv.omittedOne" : "pv.omitted", { n: String(n) })}
    </p>
  );
}

/**
 * "12 responderam · resultado só no fim da rodada" — the caption a WITHHELD word cloud or Q&A round
 * needs (US-LV-20 AC5).
 *
 * It exists because the withheld arm carries an EMPTY `words`/`entries` list rather than crossing
 * to a `tally: "hidden"` twin (that would change the frame of every round ever shipped), and an
 * empty cloud on a wall is indistinguishable from a room that answered nothing. So the fact comes
 * from the ROUND'S OWN CONFIG — `revealTally === false`, which `toPublicQuestion` does not strip —
 * and the count comes from the arm, which still carries it. No new wire field, no new i18n key:
 * `mc.tallyHidden` already says exactly this in three locales, for `mc`'s hidden tally.
 *
 * Renders nothing once the round closes or the host reveals, because then the real result is on
 * screen and the sentence would contradict it. The reveal is read off the arm's own `revealed` when
 * the frame carries it, and off the list's emptiness when it does not — see `isWithheldList`, which
 * is where that whole decision lives, and which the spoken summary shares.
 *
 * It takes the AGGREGATE and not a count, and that is the guard rather than a convenience: `mc`
 * already says this exact sentence from its own `tally: "hidden"` arm (`McPanel`, and the
 * projection's hidden block), so a caller placing this beside those would print it twice. The two
 * arms it IS for are the ones whose withheld list is empty and therefore silent.
 */
export function WithheldNote({
  question,
  closed,
  aggregate,
  className,
}: {
  question: { revealTally?: boolean } | null;
  closed: boolean;
  aggregate: QuestionAggregate | null;
  className?: string;
}): React.ReactElement | null {
  // The whole decision, including the host's reveal, lives in `isWithheldList` — shared with the
  // spoken summary so the two cannot say different things about the same round (item B).
  if (!isWithheldList(question, closed, aggregate)) return null;
  const answered = answeredIn(aggregate);
  if (answered === null) return null;
  return (
    <p className={className ?? "text-accent"}>
      {answered} {t(answered === 1 ? "mc.answeredOne" : "mc.answered")} · {t("mc.tallyHidden")}
    </p>
  );
}

/**
 * "média 7,2 · 18 respostas" — the one line a scale round is for (W9b, US-LV-5 AC2).
 *
 * ONE component for the three screens that must say it (projection, host panel, results), for the
 * same reason `WithheldNote` is one: three copies of a division are three places for the
 * denominator to drift, and the mean is the number the room quotes in the debrief.
 *
 * The BARS stay beside it — a scale's distribution is information; the mean is the headline.
 *
 * Renders nothing on a round that did not ask for an average, on a withheld tally (it withholds the
 * mean exactly as it withholds the bars), or when there is no average to state — see `meanOf`.
 *
 * TWO sources for the scale points, and the round-1 review is why: the LIVE aggregate carries none
 * (`QuestionRuntime.aggregate` publishes `counts`/`total`/`aggregate`, and the option table is
 * already on the `question` frame — putting it on both would be the same data twice on every
 * broadcast), while the PERSISTED record carries its own copy because the question is not stored.
 * So a live surface passes `options` and a record surface does not, `agg.options` wins when it is
 * there, and neither reader has to know which one it is looking at.
 */
export function MeanNote({ agg, options, className }: {
  agg: QuestionAggregate | null;
  /**
   * The round's own option table, from the `question` frame — LIVE surfaces only.
   *
   * `| undefined` spelled out, not just `?`: under `exactOptionalPropertyTypes` a caller passing
   * `state.question.options` (which IS `McOption[] | undefined`) cannot fill a merely optional
   * prop, and the alternative is every live call site guarding a value this component already
   * treats as absent.
   */
  options?: ReadonlyArray<McOption> | undefined;
  className?: string;
}): React.ReactElement | null {
  if (agg?.type !== "mc" || agg.tally !== "open" || agg.aggregate !== "mean") return null;
  const mean = meanOf(agg.counts, agg.options ?? options, agg.total);
  if (mean === null) return null;
  return (
    <p className={className ?? "text-accent"}>
      {t("scale.mean", { v: formatMean(mean, getLocale()) })} · {agg.total}{" "}
      {t(agg.total === 1 ? "mc.answeredOne" : "mc.answered")}
    </p>
  );
}

/**
 * One Q&A entry's text, with its author when the round is a named panel (US-LV-20 AC3).
 *
 * Exists because the name was reaching ONE of the three lists that render an entry: the phone had
 * it, the projector and the host's list did not — so the panel round's whole point was invisible on
 * the wall the room was looking at (round-1 review, item A). Three call sites, one renderer, and
 * the next list to be added gets it by construction.
 *
 * `author` is on the frame only when the round asked to be named, so an anonymous wall cannot grow
 * a name by a rendering mistake — and it is a NICKNAME, never a token or a pid (constitution #8).
 * The em dash rather than a "por": no sentence to translate, so no fourth i18n key to keep in sync.
 */
export function OpenEntryText({ entry }: { entry: { text: string; author?: string } }): React.ReactElement {
  return (
    <>
      {entry.text}
      {entry.author !== undefined && (
        <>
          {" — "}
          <b>{entry.author}</b>
        </>
      )}
    </>
  );
}

/**
 * The join URL as a QR, inline (W10a, US-LV-17 AC1).
 *
 * SVG, never a canvas or a PNG: a projector scales it to whatever the room's wall is without
 * blurring, it prints, and it needs no ref, no effect and no pixel ratio. ONE `<path>` of module
 * rects rather than a rect per module — a version-2 code is 625 modules, and 625 elements on a
 * screen that re-renders on every vote is a cost with no picture to show for it.
 *
 * `shapeRendering="crispEdges"` because the default anti-aliases module borders into grey, which is
 * exactly the contrast a phone camera needs at twelve metres.
 */
export function QrSvg({ url, label, className }: { url: string; label: string; className?: string }): React.ReactElement {
  const { size, path } = React.useMemo(() => {
    const { modules } = qrCreate(url, { errorCorrectionLevel: "M" });
    const n = modules.size;
    let d = "";
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (modules.data[y * n + x]) d += `M${x} ${y}h1v1h-1z`;
      }
    }
    return { size: n, path: d };
  }, [url]);
  // The quiet zone is part of the spec, not decoration: without it a scanner cannot find the
  // finder patterns against whatever the slide behind it is.
  const box = size + 8;
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${box} ${box}`}
      shapeRendering="crispEdges"
      className={className}
    >
      {/* The two hex values in the app (constitution: tokens, not hex). Deliberate: a QR is a
          machine-readable target, not a surface — a brand off-white against a brand near-black is
          the contrast ratio a phone camera fails on across a dim room. */}
      <rect width={box} height={box} fill="#fff" />
      <path d={path} transform="translate(4 4)" fill="#000" />
    </svg>
  );
}

/**
 * How the room gets in: the code in display type, the QR of the join URL, and the URL in words.
 *
 * All three, because each fails on its own — a camera loses to projector glare, a QR is unreadable
 * from the back row of a big room, and a URL typed by hand is where a wrong character costs a
 * minute. `compact` is the same card in the projection header while a round runs, so a latecomer
 * can join without the host interrupting the round.
 *
 * Renders NOTHING without a code: the `state` frame carries "" until the room has been told, and a
 * QR of an empty path is a square that scans to the wrong page.
 */
export function JoinCard({ code, compact }: { code: string; compact?: boolean }): React.ReactElement | null {
  if (!code) return null;
  // `location.origin`, not the API's `joinUrl`: the projection never sees one (no `state` frame
  // carries it), and it does not need to — on the canonical domain the two are identical, and on a
  // preview branch the preview is the deployment actually serving `/j/:code`. The PRESENTER is the
  // surface that must use the API's URL, because the host's laptop is where a localhost origin
  // would produce a QR no phone in the room can reach.
  const url = `${location.origin}/j/${code}`;
  const words = url.replace(/^https?:\/\//, "");
  const label = t("join.qrLabel", { code });
  if (compact) {
    // Sized for a ROOM, not a browser window: at 64px the compact QR is ~10cm on a 3m projected
    // image — ~3mm modules, which a phone camera resolves from about a metre. 96px doubles the
    // module size at the same distance, and the code beside it moves up a step for the same reason.
    return (
      <div className="flex items-center gap-3">
        <QrSvg url={url} label={label} className="h-24 w-24" />
        <div>
          <div className="font-display text-display-md tracking-widest">{code}</div>
          <div className="text-sm text-muted">{words}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-center gap-8">
      <QrSvg url={url} label={label} className="h-80 w-80" />
      <div className="flex flex-col gap-2">
        <div className="text-display-sm">{t("join.instruction")}</div>
        <div className="font-display text-display-xl tracking-widest">{code}</div>
        <div className="text-display-sm text-muted">{words}</div>
      </div>
    </div>
  );
}

/**
 * The one element that tells a screen-reader user the room moved.
 *
 * `polite`, never `assertive`: an assertive region interrupts, and it would talk over the question
 * itself every time a vote lands. `aria-atomic` so the whole sentence is re-read rather than the
 * diff, which is unintelligible for a changing count.
 *
 * Visually hidden, because everything it says is already on screen for anyone who can see it.
 */
export function LiveRegion({ text, label }: { text: string; label?: string }): React.ReactElement {
  return (
    <p role="status" aria-live="polite" aria-atomic="true" aria-label={label ?? t("a11y.liveResults")} className="sr-only">
      {text}
    </p>
  );
}

/**
 * Failed-fetch state for the public Kit pages. The library used to be a bundled constant that could
 * not fail; now it can, so the visitor gets a way out. Retry is a reload — on an error screen there
 * is no filter or scroll state left to preserve.
 * ponytail: reload, not a refetch nonce. Revisit if this ever renders inside a live view.
 */
export function RemoteError({ message, retry }: { message: string; retry: string }): React.ReactElement {
  return (
    <div className="flex flex-col items-start gap-2">
      <p role="alert">{message}</p>
      <button onClick={() => location.reload()} className="rounded-md border border-border px-3 py-1 text-body-sm">
        {retry}
      </button>
    </div>
  );
}

/**
 * What the host sees when the server says "not on this plan".
 *
 * Rendered from the server's own `plan`/`limit`, not from a client-side copy of the tier table —
 * the numbers in the sentence are the numbers that were actually enforced, so the message cannot
 * drift from the rule. `role="alert"` because it appears in response to an action the host just
 * took and would otherwise seem to have silently done nothing.
 */
// Keyed rather than a boolean: W11 added a third refusal to this page, and a nested ternary is
// where the fourth one would be rendered with the wrong sentence.
const LIMIT_KEY: Record<LimitError["code"], string> = {
  ROUND_LIMIT_REACHED: "rounds",
  SESSION_LIMIT_REACHED: "sessions",
  ASYNC_NOT_ON_PLAN: "async",
};

/** The refusal as ONE sentence, for a surface with no room for a banner (the Kit planner's blocks,
 *  US-MB-6). Same key, same server-supplied numbers as the banner below — one rule, two renderers. */
export function limitSentence(limit: LimitError): string {
  return t(`limit.${LIMIT_KEY[limit.code]}Body`, { plan: t(`plan.${limit.plan}`), limit: String(limit.limit) });
}

export function LimitBanner({ limit }: { limit: LimitError }): React.ReactElement {
  const kind = LIMIT_KEY[limit.code];
  return (
    <div role="alert" className="rounded-lg border border-warning p-4">
      <p className="font-bold">{t(`limit.${kind}Title`)}</p>
      <p className="mt-1 text-body-sm opacity-80">{limitSentence(limit)}</p>
      <Link to="/planos" className="mt-3 inline-block rounded-md bg-accent px-3 py-1 font-bold text-surface">
        {t("limit.cta")}
      </Link>
    </div>
  );
}

/**
 * "This room is over." Same `role="alert"` reasoning as `LimitBanner`, and deliberately without its
 * upgrade link: no plan sells a 13th hour, so pointing at /planos here would be selling a fix for
 * something a purchase cannot fix.
 */
export function SessionOverNotice({ reason }: { reason: "closed" | "expired" }): React.ReactElement {
  return (
    <div role="alert" className="rounded-lg border border-warning p-4">
      <p className="font-bold">{t("sessionOver.title")}</p>
      <p className="mt-1 text-body-sm opacity-80">{t(`sessionOver.${reason}`)}</p>
    </div>
  );
}

/**
 * Favourite toggle. `aria-pressed` rather than a checkbox because it acts on content, and the label
 * changes with state so a screen reader announces the action, not just a star glyph.
 */
export function FavouriteButton({
  active, onToggle, label,
}: { active: boolean; onToggle: () => void; label: string }): React.ReactElement {
  return (
    <button
      onClick={onToggle}
      aria-pressed={active}
      aria-label={label}
      title={label}
      className={`rounded-md px-2 py-1 text-body-sm ${active ? "text-accent" : "text-muted"}`}
    >
      {active ? "\u2605" : "\u2606"}
    </button>
  );
}
